<script setup lang="ts">
import { useTranslate } from "@/composables/useTranslate";
import { InstallationInput } from "@/types";
import { defineProps, defineEmits, ref, watch, nextTick } from "vue";

const props = defineProps<Props>();
type Props = {
  steps: any;
  selectedInput: InstallationInput | null;
  repeatableGroupIndex?: number;
  isStepsMenuOpen: boolean;
};

const emits = defineEmits<Emits>();
type Emits = {
  (event: "onToggle"): void;
  (
    event: "setSelectedInput",
    input: number,
    repeatableGroupIndex?: number
  ): void;
};

const { t } = useTranslate();

const searchByInputName = ref("");

const inputRefs = ref<Record<number, HTMLElement | any>>({});

watch(
  [props.selectedInput, props.repeatableGroupIndex],
  async ([newInput, _newRepeatableGroupIndex], [_oldInput, _oldIndex]) => {
    await nextTick();
    const el = inputRefs.value[newInput?.id];
    if (el && props.isStepsMenuOpen) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
);

const getParentInputName = (stepId: number, currentInput: any) => {
  let step = props.steps.find((step: any) => step.id === stepId);
  let parentInput = step.inputs.find(
    (input: any) => input.id === currentInput.parentId
  );
  if (!parentInput) {
    return "";
  }

  if (parentInput.type === "repeatable_group") {
    let repeatableGroupDetails = parentInput.repeatableGroupDetails.find(
      (detail: any) => detail.order === currentInput.repeatableGroupIndex + 1
    );
    return repeatableGroupDetails.name;
  }
  return parentInput.name;
};
</script>

<template>
  <div
    class="h-[calc(100vh-90px)] bg-[#F9F8F6] rounded-primary-radius py-2 transition-all duration-200 ease-in-out overflow-hidden"
    :class="{
      'w-[239px]': isStepsMenuOpen,
      'w-0 !p-0 ': !isStepsMenuOpen,
      'opacity-100': isStepsMenuOpen,
      'opacity-0': !isStepsMenuOpen,
    }"
  >
    <!-- Header -->
    <div
      v-if="isStepsMenuOpen"
      class="w-full flex px-2 items-center justify-between gap-2 border-b boder-b-[#898790] pb-[5px]"
    >
      <!-- Form -->
      <div class="w-full flex items-center gap-2">
        <img src="@/assets/search-icon.svg" alt="" />
        <input
          v-model="searchByInputName"
          class="w-full bg-transparent outline-none text-[#898790] text-sm tracking-[0.01rem] font-medium"
          type="text"
          :placeholder="t('focus_mode_modal.substeps').value"
        />
      </div>

      <!-- Toggler -->
      <button
        @click="emits('onToggle')"
        class="flex items-center justify-center"
      >
        <img
          class="max-w-[16px] max-h-[16px] min-w-[16px] min-h-[16px]"
          src="@/assets/toggle-icon.svg"
          alt="Toggle"
        />
      </button>
    </div>
    <!-- Steps -->
    <div
      v-if="isStepsMenuOpen"
      class="h-[calc(100vh-132px)] px-2 overflow-y-auto flex flex-col gap-[2px]"
    >
      <div
        v-for="step, index in steps.filter((step:any)=> step.inputs.filter((input:any) =>
        (input.type === 'image' || input.subtype === 'image') && input.value && input.value.length > 0 && input.name.toLowerCase().includes(searchByInputName.trim().toLowerCase())).length > 0)"
        :key="step.id"
        :class="index === 0 ? 'mt-2' : 'mt-[10px]'"
      >
        <h4
          class="text-primary-text-color text-sm font-medium tracking-[0.01rem]"
        >
          {{ step.name }}
        </h4>
        <div
          class="border-l-2 ml-2 mt-1"
          :class="selectedInput?.id === step.inputs.find((item: any) => item.id)?.id ? 'border-l-[#D3D1CD]' : 'border-l-[#E3E1EA]'"
        >
          <div
            v-for="input in step.inputs.filter((input:any)=> (input.type === 'image' || input.subtype === 'image') && input.value && input.value.length > 0 && input.name.toLowerCase().includes(searchByInputName.trim().toLowerCase()))"
            :key="input.id"
            class="relative ml-[6px] rounded-primary-radius px-[6px] py-[2px] cursor-pointer duration-300"
            :class="`${
              selectedInput?.id === input.id &&
              (repeatableGroupIndex !== null &&
              repeatableGroupIndex !== undefined
                ? input.repeatableGroupIndex === repeatableGroupIndex
                : true)
                ? 'bg-[#DFDEDC]'
                : 'bg-transparent'
            }`"
            @click="
              $emit('setSelectedInput', input.id, input.repeatableGroupIndex)
            "
            :ref="(el) => (inputRefs[input.id] = el)"
          >
            <span
              class="text-sm text-primary-text-color font-normal leading-5"
              :class="
                selectedInput?.id === input.id &&
                (repeatableGroupIndex !== null &&
                repeatableGroupIndex !== undefined
                  ? input.repeatableGroupIndex === repeatableGroupIndex
                  : true)
                  ? 'before:absolute before:top-0 before:-left-2 before:h-full before:w-[2px] before:bg-[#70899C]'
                  : ''
              "
            >
              {{
                input.parentId ? getParentInputName(step.id, input) + " /" : ""
              }}
              {{ input.name }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
